import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import validate from 'validate.js';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/styles';
import {
	Button, TextField, Typography, Grid, Select,
	Card, CardHeader, CardContent, Divider, Accordion,
	AccordionSummary, AccordionDetails, FormGroup, Switch
} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import useRouter from 'utils/useRouter';
import _ from 'underscore';
import L from 'leaflet';
import { Page, Header, Map, SelectField, Label, SearchBar, FiltersDrawer, DatePicker } from 'components';
import DateTimePicker from 'components/DatePicker/DateTimePicker.js';
import { SetFormState, ToggleLayer3Dialog, GetLayer3Failure } from '../../../../../actions/VoiceActions';
import VoiceMocMtcDataTable from './components/VoiceMocMtcDataTable';
import VoiceComments from './components/VoiceComments';
import Layer3 from './components/Layer3/Layer3Dialog';

const schema = {
	firstname: {
		presence: { allowEmpty: false, message: 'is required' },
	},
	lastname: {
		presence: { allowEmpty: false, message: 'is required' },
	},
	email: {
		presence: { allowEmpty: false, message: 'is required' },
		email: true,
	},
	roleId: {
		presence: { allowEmpty: false, message: 'is required' },
	},
};

const useStyles = makeStyles((theme) => ({
	root: {},
	fields: {
		margin: theme.spacing(-1),
		display: 'flex',
		flexWrap: 'wrap',
		'& > *': {
			flexGrow: 1,
			margin: theme.spacing(1),
		},
	},
	submitButton: {
		marginTop: theme.spacing(2),
		width: '100%',
	},
	divider: {
		marginTop: theme.spacing(2),
		marginBottom: theme.spacing(2),
	}
}));

const VoiceEditFailureDialogForm = (props) => {
	const { className, tableId, ...rest } = props;

	const classes = useStyles();
	const router = useRouter();
	const dispatch = useDispatch();
	const setFormState = formState => dispatch(SetFormState(formState));
	const formState = useSelector((state) => state.voiceReducer.formState);
	const userId = useSelector(state => state.session.user.id);
	const role = useSelector(state => state.session.user.role);

	const [hideFields, setHideFields] = React.useState(false);
	const failure = useSelector((state) => state.voiceReducer.failure);
	const comments = useSelector((state) => state.voiceReducer.comments.trackerVoiceCommentsDto);

	const layer3DialogVisibility = useSelector((state) => state.voiceReducer.layer3DialogVisibility);
	const layer3Failure = useSelector((state) => state.voiceReducer.layer3Failure);

	const maps = useSelector(state => state.mapReducer.maps);
	const mapOne = maps.filter(item => item.id === 'failures-map-details')[0];

	// const [stateValid, setStateValid] = React.useState(true);

	const _handleChange = (event) => {
		event.persist();
		setFormState({
			...formState,
			values: {
				...formState.values,
				[event.target.name]:
					event.target.type === 'checkbox'
						? event.target.checked
						: event.target.value,
			},
			touched: {
				...formState.touched,
				[event.target.name]: true,
			},
		});
	};

	const _handleDateChange = (value, name) => {
		setFormState({
			...formState,
			values: {
				...formState.values,
				[name]:
					value,
			},
			touched: {
				...formState.touched,
				[name]: true,
			},
		});
	};

	// const handleChangeValid = (event) => {
	// 	setStateValid(!stateValid);

	// 	setFormState({
	// 		...formState,
	// 		values: {
	// 			...formState.values,
	// 			valid: event.target.checked
	// 		}
	// 	});
	// };

	const _hasError = (field) => (!!(formState.touched[field] && formState.errors[field]));

	const makeDetailsTable = (item) => {
		const table = `<table class="table table-striped">
							<tr>
							<th>Failure Id: </th>
							<td>${item.failureId}</td>
							</tr>
							<tr>
							<th>Latitude: </th>
							<td>${item.latitude}</td>
							</tr>
							<tr>
							<th>Longitude: </th>
							<td>${item.longitude}</td>
							</tr>
						</table>`;
		return table;
	};

	const _addLayerFailures = (map, layerData, type, fitBounds) => {
		map.RemoveLayerById('failures');
		const markers = [];

		const voiceIconBlocked = L.icon({
			iconUrl: '/images/failuresIcons/eventsvoicecallattempt_o2.png',
			iconSize: [35, 35], // size of the icon
			iconAnchor: [16, 35], // point of the icon which will correspond to marker's location
			popupAnchor: [0, -35] // point from which the popup should open relative to the iconAnchor
		});

		const voiceIconBlockedQoe = L.icon({
			iconUrl: '/images/failuresIcons/eventsvoiceblockcallQoe_o2.png',
			iconSize: [35, 35], // size of the icon
			iconAnchor: [16, 35], // point of the icon which will correspond to marker's location
			popupAnchor: [0, -35] // point from which the popup should open relative to the iconAnchor
		});

		const voiceIconDropped = L.icon({
			iconUrl: '/images/failuresIcons/eventsvoicedropcall_o2.png',
			iconSize: [35, 35], // size of the icon
			iconAnchor: [16, 35], // point of the icon which will correspond to marker's location
			popupAnchor: [0, -35] // point from which the popup should open relative to the iconAnchor
		});

		const voiceIconDroppedQoe = L.icon({
			iconUrl: 'images/failuresIcons/eventsvoicedropcallQoe_o2.png',
			iconSize: [35, 35], // size of the icon
			iconAnchor: [16, 35], // point of the icon which will correspond to marker's location
			popupAnchor: [0, -35] // point from which the popup should open relative to the iconAnchor
		});

		const packetIcondrop = L.icon({
			iconUrl: 'images/failuresIcons/eventspacketdropcall_o2.png',
			iconSize: [35, 35], // size of the icon
			iconAnchor: [16, 35], // point of the icon which will correspond to marker's location
			popupAnchor: [0, -32] // point from which the popup should open relative to the iconAnchor
		});

		if (!_.isEmpty(layerData)) {
			layerData.forEach(item => {
				if (type.includes('voice')) {
					if (item.failureType === 'Blocked Call') {
						const marker = L.marker([item.latitude, item.longitude], { title: item.failureId, icon: voiceIconBlocked });
						markers.push(marker.bindPopup(`<br>${makeDetailsTable(item)}<br>`, { autoPan: false }));
					} else if (item.failureType === 'QoE Call Setup Time') {
						const marker = L.marker([item.latitude, item.longitude], { title: item.failureId, icon: voiceIconBlockedQoe });
						markers.push(marker.bindPopup(`<br>${makeDetailsTable(item)}<br>`, { autoPan: false }));
					} else if (item.failureType === 'Dropped Call') {
						const marker = L.marker([item.latitude, item.longitude], { title: item.failureId, icon: voiceIconDropped });
						markers.push(marker.bindPopup(`<br>${makeDetailsTable(item)}<br>`, { autoPan: false }));
					} else {
						const marker = L.marker([item.latitude, item.longitude], { title: item.failureId, icon: voiceIconDroppedQoe });
						markers.push(marker.bindPopup(`<br>${makeDetailsTable(item)}<br>`, { autoPan: false }));
					}
				} else {
					const marker = L.marker([item.latitude, item.longitude], { title: item.failureId, icon: packetIcondrop });
					markers.push(marker.bindPopup(`<br>${makeDetailsTable(item)}<br>`, { autoPan: false }));
				}

				// const marker = L.marker([item.latitude, item.longitude], { title: item.failureId, icon: voiceIconBlocked });
				// markers.push(marker.bindPopup(`<br>${makeDetailsTable(item)}<br>`, { autoPan: false }));
			});

			const layer = L.layerGroup(markers, { id: 'failures' });
			layer.addTo(map.mapContext.map);
			const group = new L.featureGroup(markers);
			if (fitBounds) {
				map.mapContext.map.fitBounds(group.getBounds());
			}
		}
		// dispatch(onUpdateLegend({ id: map.mapContext.id, newLegend: legend }));
	};

	const _openLayer3 = () => {
		dispatch(ToggleLayer3Dialog(true));
	};

	const _layer3DialogClose = () => {
		dispatch(ToggleLayer3Dialog(false));
	};

	useMemo(() => {
		const errors = validate(formState.values, schema);

		setFormState({
			...formState,
			isValid: !errors,
			errors: errors || {},
		});

		if (role === 'Client') {
			setHideFields(true);
		} else {
			setHideFields(false);
		}

		// if (formState.values.valid) {
		// 	setStateValid(true);
		// } else {
		// 	setStateValid(false);
		// }
	}, [formState.values]);

	useMemo(() => {
		if (mapOne) {
			_addLayerFailures(mapOne.control, layer3Failure, 'voice', true);
		}
	}, [layer3Failure]);

	return (
		<div className={classes.fields}>
			{/* New row */}
			<Grid container spacing={4}>
				<Grid item md={12} xs={12}>
					<Card>
						<Grid container spacing={1}>
							<Grid item md={10} xs={10}>
								<CardHeader
									title={`Failure: ${formState.values.failureId || ''}`}
								/>
							</Grid>
							{/* <Grid item md={2} xs={2} container justify="flex-end">
								<div hidden={hideFields}>
									<FormGroup>
										<Typography component="div">
											<Grid component="label" container alignItems="center" spacing={1}>
												<Grid item>Invalid</Grid>
												<Grid item>
													<Switch
														checked={stateValid}
														onChange={handleChangeValid}
														color="primary"
														name="checkedValid"
														inputProps={{ 'aria-label': 'primary checkbox' }}
													/>
												</Grid>
												<Grid item>Valid&nbsp;</Grid>
											</Grid>
										</Typography>
									</FormGroup>
								</div>
							</Grid> */}
						</Grid>
						<Divider />
						<CardContent>
							<Grid container spacing={4}>
								<Grid item md={2} xs={2} />
								{/* <Grid item md={2} xs={2}>
									<TextField
										id="failureSourceHeader"
										label="Source"
										name="failureSource"
										InputProps={{
											readOnly: true,
										}}
										variant="outlined"
										error={_hasError('failureSource')}
										fullWidth
										helperText={
											_hasError('failureSource') ? formState.errors.failureSource[0] : null
										}
										// onChange={_handleChange}
										value={formState.values.failureSource || ''}
									/>
								</Grid> */}
								<Grid item md={2} xs={2}>
									<TextField
										id="failureTypeHeader"
										label="Failure Type"
										name="failureType"
										InputProps={{
											readOnly: true,
										}}
										variant="outlined"
										error={_hasError('failureType')}
										fullWidth
										helperText={
											_hasError('failureType') ? formState.errors.failureType[0] : null
										}
										// onChange={_handleChange}
										value={formState.values.failureType || ''}
									/>
								</Grid>
								<Grid item md={2} xs={2}>
									<TextField
										id="failureSideHeader"
										label="Failure Side"
										name="failureSide"
										InputProps={{
											readOnly: true,
										}}
										variant="outlined"
										error={_hasError('failureSide')}
										fullWidth
										helperText={
											_hasError('failureSide') ? formState.errors.failureSide[0] : null
										}
										// onChange={_handleChange}
										value={formState.values.failureSide || ''}
									/>
								</Grid>
								<Grid item md={2} xs={2}>
									<TextField
										id="mobilityScenarioHeader"
										label="Mobility scenario"
										name="mobilityScenario"
										InputProps={{
											readOnly: true,
										}}
										variant="outlined"
										error={_hasError('mobilityScenario')}
										fullWidth
										helperText={
											_hasError('mobilityScenario') ? formState.errors.mobilityScenario[0] : null
										}
										// onChange={_handleChange}
										value={formState.values.mobilityScenario || ''}
									/>
								</Grid>
								<Grid item md={2} xs={2}>
									<TextField
										id="testTypeHeader"
										label="Test type"
										name="testType"
										InputProps={{
											readOnly: true,
										}}
										variant="outlined"
										error={_hasError('testType')}
										fullWidth
										helperText={
											_hasError('testType') ? formState.errors.testType[0] : null
										}
										// onChange={_handleChange}
										value={formState.values.testType || ''}
									/>
								</Grid>
								<Grid item md={1} xs={1} />
							</Grid>
						</CardContent>
					</Card>
				</Grid>
			</Grid>
			{/* New row */}
			<Grid container spacing={4}>
				<Grid item md={6} xs={6}>
					<Card>
						<CardHeader title="General" />
						<Divider />
						<CardContent>
							{/* <Grid container spacing={4}>
								<Grid item md={3} xs={12}>
									<TextField
										id="csr"
										label="CSR"
										name="csr"
										InputProps={{
											readOnly: true,
										}}
										// variant="outlined"
										error={_hasError('csr')}
										fullWidth
										helperText={
											_hasError('csr') ? formState.errors.csr[0] : null
										}
										// onChange={_handleChange}
										value={formState.values.csr || ''}
									/>
								</Grid>
								<Grid item md={3} xs={12}>
									<TextField
										id="reallocatedCount"
										label="Times reallocated"
										name="reallocatedCount"
										InputProps={{
											readOnly: true,
										}}
										// variant="outlined"
										error={_hasError('reallocatedCount')}
										fullWidth
										helperText={
											_hasError('reallocatedCount') ? formState.errors.reallocatedCount[0] : null
										}
										// onChange={_handleChange}
										value={formState.values.reallocatedCount || ''}
									/>
								</Grid>
							</Grid> */}
							{/* <Divider className={classes.divider} variant="middle" /> */}
							<Grid container spacing={4}>
								<Grid item md={3} xs={3}>
									<TextField
										id="initialClassification"
										label="Initial classification"
										name="initialClassification"
										InputProps={{
											readOnly: true,
										}}
										variant="outlined"
										error={_hasError('initialClassification')}
										fullWidth
										helperText={
											_hasError('initialClassification') ? formState.errors.initialClassification[0] : null
										}
										// onChange={_handleChange}
										value={formState.values.initialClassification || ''}
									/>
								</Grid>
								<Grid item md={3} xs={3}>
									<TextField
										id="initialTimestamp"
										label="Timestamp"
										name="initialTimestamp"
										InputProps={{
											readOnly: true,
										}}
										variant="outlined"
										error={_hasError('initialTimestamp')}
										fullWidth
										helperText={
											_hasError('initialTimestamp') ? formState.errors.initialTimestamp[0] : null
										}
										// onChange={_handleChange}
										value={formState.values.initialTimestamp || ''}
									/>
								</Grid>
								<Grid item md={3} xs={3}>
									<TextField
										id="week"
										label="Week"
										name="week"
										InputProps={{
											readOnly: true,
										}}
										variant="outlined"
										error={_hasError('week')}
										fullWidth
										helperText={
											_hasError('week') ? formState.errors.week[0] : null
										}
										// onChange={_handleChange}
										value={formState.values.week || ''}
									/>
								</Grid>
								<Grid item md={3} xs={3}>
									<TextField
										id="incNumber"
										label="Incident Number"
										name="incNumber"
										disabled={(failure && failure.incNumber) == null}
										InputProps={{
											readOnly: true,
										}}
										variant="outlined"
										error={_hasError('incNumber')}
										fullWidth
										helperText={
											_hasError('incNumber') ? failure.incNumber : null
										}
										// onChange={_handleChange}
										value={(failure && failure.incNumber) || ''}
									/>
								</Grid>
							</Grid>
						</CardContent>
					</Card>
					<Card>
						<CardHeader title="Layer 3" />
						<Divider />
						<CardContent>
							<Grid container spacing={4}>
								<Grid item md={12} xs={12}>
									<Map id="failures-map-details" scale height="20vh" />
								</Grid>
								<Grid item md={3} xs={3}>
									<Button
										color="primary"
										onClick={_openLayer3}
										variant="contained"
									>
										Open Layer 3
									</Button>
									{layer3DialogVisibility && (
										<Layer3
											open={layer3DialogVisibility}
											onClose={_layer3DialogClose}

										/>
									)}
								</Grid>
							</Grid>
							{/* <Grid container spacing={4}>
								<Grid item md={12} xs={12}>
									<div style={{ height: '10vh' }}>
										<div className={clsx(classes.container)} >
											<Map id="failures-map" scale />
										</div>
									</div>
								</Grid>
							</Grid> */}
						</CardContent>
					</Card>
				</Grid>
				<Grid item md={6} xs={6}>
					<Card>
						<CardHeader title="MOC and MTC Information" />
						<Divider />
						<CardContent>
							<Grid container spacing={4}>
								<Grid item md={12} xs={12}>
									<VoiceMocMtcDataTable data={(failure && failure.trackerVoiceMocMtcDto) || []} />
								</Grid>
							</Grid>
						</CardContent>
					</Card>
				</Grid>
			</Grid>
			{/* New row */}
			<Grid container spacing={4}>
				<Grid item md={6} xs={6} hidden={hideFields}>
					<VoiceComments tableId={tableId} comments={(comments) || []} />
				</Grid>
				<Grid item md={6} xs={6}>
					<Card>
						<CardHeader title="Failure correction" />
						<Divider />
						<CardContent>
							<Grid item md={12} xs={12}>
								<Accordion defaultExpanded>
									<AccordionSummary
										expandIcon={<ExpandMoreIcon />}
										aria-controls="panel1a-content"
										id="panel1a-header"
									>
										<Typography>Levels</Typography>
									</AccordionSummary>
									<AccordionDetails>
										<Grid container direction="vertical" spacing={4}>
											<Grid item md={6} xs={6}>
												<TextField
													id="cfn1Level"
													name="cfn1Level"
													label="1st Level"
													defaultValue=""
													InputProps={{
														readOnly: true,
													}}
													variant="outlined"
													fullWidth
													value={formState.values.cfn1Level || ''}
												/>
											</Grid>
											<Grid item md={6} xs={6}>
												<SelectField
													id="cfn2Level"
													name="cfn2Level"
													hintText="2nd Level"
													defaultValue=""
													menuItems={(failure && failure.cfn2LevelList) || []}
													onChange={_handleChange}
													value={formState.values.cfn2Level || ''}
												/>
											</Grid>
											<Grid item md={6} xs={6}>
												<SelectField
													id="cfn3Level"
													name="cfn3Level"
													hintText="3rd Level"
													defaultValue=""
													menuItems={(failure && failure.cfn3LevelList) || []}
													onChange={_handleChange}
													value={formState.values.cfn3Level || ''}
												/>
											</Grid>
											{/* <Grid item md={6} xs={6}>
												<SelectField
													id="reallocatedLevel"
													hintText="Reallocated Level"
													defaultValue=""
													menuItems={[]}
													onChange={_handleChange}
												/>
											</Grid> */}
											<Grid item md={6} xs={6}>
												<SelectField
													id="finalClassification"
													hintText="Final Classification"
													defaultValue=""
													menuItems={[]}
													readOnly
												/>
											</Grid>
											<Grid item md={6} xs={6}>
												<TextField
													id="failureSide"
													label="Failure Side"
													defaultValue=""
													InputProps={{
														readOnly: true,
													}}
													variant="outlined"
													fullWidth
													value={formState.values.failureSide || ''}
												/>
											</Grid>
											<Grid item md={6} xs={6}>
												<SelectField
													id="failureType"
													name="failureType"
													hintText="Failure Type"
													defaultValue=""
													menuItems={(failure && failure.failureTypeList) || []}
													onChange={_handleChange}
													value={formState.values.failureType || ''}
												/>
											</Grid>
										</Grid>
									</AccordionDetails>
								</Accordion>
								<Accordion defaultExpanded hidden={hideFields}>
									<AccordionSummary
										expandIcon={<ExpandMoreIcon />}
										aria-controls="panel1a-content"
										id="panel1a-header"
									>
										<Typography>Solution</Typography>
									</AccordionSummary>
									<AccordionDetails>
										<Grid container direction="vertical" spacing={4}>
											{/* <Grid item md={6} xs={6}>
												<SelectField
													id="status"
													name="status"
													hintText="Status"
													defaultValue=""
													menuItems={(failure && failure.statusList) || []}
													value={formState.values.status || ''}
													onChange={_handleChange}
												/>
											</Grid> */}
											<Grid item md={6} xs={6}>
												<SelectField
													id="solution"
													name="solution"
													hintText="Solution"
													defaultValue=""
													menuItems={(failure && failure.solutionList) || []}
													value={formState.values.solution || ''}
													onChange={_handleChange}
												/>
											</Grid>
											{/* <Grid item md={6} xs={6}>
												<SelectField
													id="solutionOwner"
													name="solutionOwner"
													hintText="Solution Owner"
													defaultValue=""
													menuItems={(failure && failure.solutionOwnerList) || []}
													value={formState.values.solutionOwner || ''}
													onChange={_handleChange}
												/>
											</Grid>
											<Grid item md={6} xs={6}>
												<TextField
													id="owner"
													label="Owner"
													defaultValue=""
													InputProps={{
														readOnly: true,
													}}
													variant="outlined"
													fullWidth
												/>
											</Grid> */}
											<Grid item md={12} xs={12}>
												<TextField
													id="solutionDescription"
													name="solutionDescription"
													label="Description"
													defaultValue=""
													value={formState.values.solutionDescription || ''}
													fullWidth
													onChange={_handleChange}
												/>
											</Grid>
											{/* <Grid item md={6} xs={6}>
												<DateTimePicker
													id="expectedSolutionDate"
													name="expectedSolutionDate"
													label="Expected Solution Date"
													style={{ width: '100%' }}
													value={formState.values.expectedSolutionDate || ''}
													onChange={(value) => _handleDateChange(value, 'expectedSolutionDate')}
													minutesStep={1}
												/>
											</Grid>
											<Grid item md={6} xs={6}>
												<DateTimePicker
													id="actualSolutionDate"
													name="actualSolutionDate"
													label="Actual Solution Date"
													style={{ width: '100%' }}
													value={formState.values.actualSolutionDate || ''}
													onChange={(value) => _handleDateChange(value, 'actualSolutionDate')}
													minutesStep={1}
												/>
											</Grid> */}
										</Grid>
									</AccordionDetails>
								</Accordion>
							</Grid>
						</CardContent>
					</Card>
				</Grid>
			</Grid>
		</div>
	);
};

VoiceEditFailureDialogForm.propTypes = {
	className: PropTypes.string,
	tableId: PropTypes.string
};

export default VoiceEditFailureDialogForm;
